import { Component, OnInit } from '@angular/core';
import { AbstractControl, NgControl, FormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	map,
	shareReplay,
	startWith,
	switchMap,
	take,
	takeUntil,
} from 'rxjs/operators';
import { FormConfig, CustomFormAccessor } from '../form-cva.component';
import { Address } from 'src/shared/api';
import { LookUpsService } from 'src/shared/lookups.service';
import { DEFAULT_COUNTRY_CODE } from 'src/shared/constants';
 
const addressFormConfig: FormConfig<Address> = {
	id: [''],
	address1: ['', [Validators.required]],
	address2: [''],
	city: ['', [Validators.required]],
	state: ['', [Validators.required]],
	zipCode: [''],
	country: [DEFAULT_COUNTRY_CODE, [Validators.required]],
	description: [''],
};
 
@Component({
	selector: 'rpshared-address',
	templateUrl: './address.component.html',
})
export class AddressComponent
	extends CustomFormAccessor<Address>
	implements OnInit
{
	readonly countries$ = this.lookupsService.countries;
 
	private readonly _countryCode = new BehaviorSubject<string>(
		DEFAULT_COUNTRY_CODE
	);

	private readonly regionsByCountry$ = this._countryCode.pipe(
		map((code) => code || DEFAULT_COUNTRY_CODE),
		distinctUntilChanged(),
		switchMap((code) =>
			this.lookupsService
				.regionsForCountry(code)
				.pipe(map((regions) => ({ code, regions })))
		),
		shareReplay(1)
	);
 
	readonly regions$ = this.regionsByCountry$.pipe(
		map(({ regions }) => regions)
	);
 
	readonly filteredStates$ = combineLatest([
		this.regions$,
		this.form.get('state').valueChanges.pipe(
			startWith(''),
			map((s) => (s ? String(s).toLowerCase() : ''))
		),
	]).pipe(
		map(([regions, s]) =>
			regions
				.filter((r) => r.regionCode.toLowerCase().includes(s))
				.sort(
					(r1, r2) =>
						Number(r2.regionCode.toLowerCase().startsWith(s)) -
						Number(r1.regionCode.toLowerCase().startsWith(s))
				)
				.map((r) => ({ code: r.regionCode, name: r.regionName }))
		)
	);
 
	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		private readonly lookupsService: LookUpsService
	) {
		super(ngControl, fb, addressFormConfig);
	}
 
	private readonly isValidRegionOfSelectedCountry = (
		control: AbstractControl
	) => {
		if (!control.value) {
			return of(null);
		}
		const countryCode =
			this.form.get('country').value || DEFAULT_COUNTRY_CODE;
		const value = String(control.value).toUpperCase();
 
		return this.regionsByCountry$.pipe(
			filter(({ code }) => code === countryCode),
			take(1),
			map(({ regions }) =>
				regions.some((r) => r.regionCode.toUpperCase() === value)
					? null
					: { invalidItem: true }
			)
		);
	};
 
	ngOnInit() {
		this.form
			.get('state')
			.setAsyncValidators(this.isValidRegionOfSelectedCountry);
 
		this.form
			.get('country')
			.valueChanges.pipe(takeUntil(this.destroyed))
			.subscribe((code) => {
				this._countryCode.next(code);
				this.form
					.get('state')
					.updateValueAndValidity({ emitEvent: false });
			});
 
		super.ngOnInit();
	}
 
	onCountryChanged() {
		this.form.get('state').setValue('');
	}
 
	// model --> view
	writeValue(value: Address): void {
		if (value) {
			const country = value.country || DEFAULT_COUNTRY_CODE;
			super.writeValue({ ...value, country });
			this._countryCode.next(country);
			this.form
				.get('state')
				.updateValueAndValidity({ emitEvent: false });
		} else {
			super.writeValue(value);
		}
	}
}
